import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import startCase from 'lodash/startCase'
import {isClient} from 'hec/utils/common'

/**
 * Converts a string to lowercase, encodes it in Base64, hashes it using SHA-256,
 * and returns the hash as a hexadecimal string.
 *
 * @param {string} string - The input string to be hashed.
 * @returns {Promise<string>} A promise that resolves to the hashed string in hexadecimal format.
 */

export const lowercaseAndHash = async (string) => {
    if (!string) return ''
    const lowercased = string.toLowerCase()
    const encoder = new TextEncoder()
    const data = encoder.encode(lowercased)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)

    return Array.from(new Uint8Array(hashBuffer))
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('')
}

/**
 * Removes the word "STORE" (case-insensitive) from the input string.
 *
 * @param {string} inputString - The string to format.
 * @returns {string} The formatted string with "STORE" removed.
 */

export const formatSiteName = (inputString) => inputString.replace(/STORE/i, '')

/**
 * Retrieves the site ID from the app configuration.
 *
 * @returns {string} The site ID.
 */
export const getSiteId = () => {
    return getConfig().app.commerceAPI.parameters.siteId
}

/**
 * Generates a unique user ID by hashing the provided email.
 *
 * @param {string} email - The user's email address.
 * @returns {Promise<string|undefined>} A promise that resolves to the hashed email string.
 * If an error occurs during hashing, it logs the error and returns undefined.
 */

export const generateUserId = async (email) => {
    try {
        const hashedEmail = await lowercaseAndHash(email)
        return hashedEmail
    } catch (error) {
        console.error('Error hashing email:', error)
    }
}

/**
 * Determines the device type based on the user agent string.
 *
 * @returns {string} The detected device type: 'mobile', 'tablet', or 'desktop'.
 */

export const getDeviceType = () => {
    let deviceType = 'desktop'
    const userAgent = navigator.userAgent

    if (!userAgent) {
        return deviceType
    }

    if (userAgent.indexOf('iPhone') > -1) {
        deviceType = 'mobile'
    } else if (
        userAgent.indexOf('iPad') > -1 ||
        (userAgent.indexOf('Macintosh') > -1 && navigator.maxTouchPoints > 1)
    ) {
        deviceType = 'tablet'
    } else if (userAgent.indexOf('Android') > -1) {
        if (userAgent.toLowerCase().indexOf('mobile') > -1) {
            deviceType = 'mobile'
        } else {
            deviceType = 'tablet'
        }
    }

    return deviceType
}

/**
 * Formats SEO metadata from Builder page data object.
 *
 * @param {object} pageData - The page data containing SEO metadata.
 * @param {string} pageData.title - The page title.
 * @param {string} pageData.description - The page description.
 * @param {string[]} pageData.keywords - An array of keywords.
 * @param {boolean} pageData.noIndex - Whether the page should not be indexed.
 * @returns {object|null} An object containing formatted SEO metadata, or null if no page data is provided.
 */

export const getSeoPageData = (pageData) => {
    if (!pageData) return null
    const {title, description, keywords, noIndex} = pageData
    const seoData = {
        title: title ? startCase(title) : '',
        description: description || '',
        keywords: keywords?.join(', ') || '',
        noIndex: !!noIndex
    }
    return seoData
}

/**
 * get formatted phone with country code
 * @param {string} phoneNumber : phone number
 * @param {string} countryName : country name 2 digit format
 * @return {string} formattedNumber
 */

export const getFormattedPhoneWithCC = (phoneNumber, countryName) => {
    const countryCodes = {
        US: '1',
        CA: '1'
    }
    const countryCode = countryCodes[countryName.toUpperCase()]
    const cleanedNumber = phoneNumber.replace(/[^\d]/g, '')
    const formattedNumber = '+' + countryCode + cleanedNumber
    return formattedNumber
}

export const updateLearningCenterOnServer = async (
    customerId,
    data,
    updateCustomerMutation,
    toast,
    formatMessage
) => {
    let bodyText = ''
    try {
        bodyText = JSON.stringify(data)
    } catch (error) {
        console.error('Error while stringifying Learning Center request body', error)
        throw new Error('Failed to stringify Learning Center request body')
    }
    const tpaLearningCenterConsentData = data.tpaLearningCenterConsentData

    return new Promise((resolve, reject) => {
        updateCustomerMutation.mutate(
            {
                parameters: {customerId},
                body: {
                    c_learningCenterData: bodyText,
                    c_tpaLearningCenterConsentData: tpaLearningCenterConsentData
                }
            },
            {
                onSuccess: (response) => {
                    toast({
                        title: formatMessage({
                            defaultMessage: 'Profile updated',
                            id: 'profile_card.info.profile_updated'
                        }),
                        status: 'success',
                        isClosable: true
                    })

                    try {
                        const parsed = JSON.parse(response.c_learningCenterData)
                        resolve({success: true, data: parsed})
                    } catch (err) {
                        reject({
                            success: false,
                            errorMsg: 'Failed to parse server response',
                            errorDetail: err
                        })
                    }
                },
                onError: (error) => {
                    toast({
                        title: formatMessage({
                            defaultMessage: 'Something went wrong. Try again!',
                            id: 'global.error.something_went_wrong'
                        }),
                        status: 'error',
                        isClosable: true
                    })
                    reject({
                        success: false,
                        errorMsg: 'Failed to update profile',
                        errorDetail: error
                    })
                }
            }
        )
    })
}

export const updateAcLoyaltyDataOnServer = async (
    customerId,
    acLoyaltyData,
    updateCustomerMutation,
    toast,
    formatMessage
) => {
    let bodyText = ''
    try {
        bodyText = JSON.stringify(acLoyaltyData)
    } catch (error) {
        console.error('Error while stringifying acLoyaltyData request body', error)
        throw new Error('Failed to stringify acLoyaltyData request body')
    }

    return new Promise((resolve, reject) => {
        updateCustomerMutation.mutate(
            {
                parameters: {customerId},
                body: {
                    c_acLoyaltyData: bodyText
                }
            },
            {
                onSuccess: (response) => {
                    toast({
                        title: formatMessage({
                            defaultMessage: 'Loyalty data updated',
                            id: 'profile_card.info.loyalty_data_updated'
                        }),
                        status: 'success',
                        isClosable: true
                    })

                    try {
                        const parsed = JSON.parse(response.c_acLoyaltyData)
                        resolve({success: true, data: parsed})
                    } catch (err) {
                        reject({
                            success: false,
                            errorMsg: 'Failed to parse server response',
                            errorDetail: err
                        })
                    }
                },
                onError: (error) => {
                    toast({
                        title: formatMessage({
                            defaultMessage: 'Something went wrong. Try again!',
                            id: 'global.error.something_went_wrong'
                        }),
                        status: 'error',
                        isClosable: true
                    })
                    reject({
                        success: false,
                        errorMsg: 'Failed to update loyalty data',
                        errorDetail: error
                    })
                }
            }
        )
    })
}

const LS_KEY = 'guestLearningCenterData'

export const hasGuestLearningCenterData = () => {
    return localStorage.getItem(LS_KEY) !== null
}

export const getGuestLearningData = () => {
    try {
        const data = localStorage.getItem(LS_KEY)
        return data ? JSON.parse(data) : {goals: [], activeGoal: ''}
    } catch {
        return {goals: [], activeGoal: ''}
    }
}

export const saveGuestLearningData = (data) => {
    try {
        localStorage.setItem(LS_KEY, JSON.stringify(data))
    } catch {
        // fail
    }
}

const LS_TPA_CONSENT_KEY = 'tpaLearningCenterConsentData'

export const getLearningCenterConsent = () => {
    try {
        const data = localStorage.getItem(LS_TPA_CONSENT_KEY)
        return data ? JSON.parse(data) : null
    } catch {
        return null
    }
}

export const saveLearningCenterConsent = (data) => {
    try {
        localStorage.setItem(LS_TPA_CONSENT_KEY, JSON.stringify(data))
    } catch {
        // fail
    }
}

export const checkForURLParams = (param) => {
    if (!isClient) return false
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.has(param)
}
